// Copyright 2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import { AuthType } from "../enums";
import { CONFIG_ELEMENTS } from "../constants";
import log from "../logging";
import { ConfigurationOptions } from "aws-sdk/lib/config-base";
import { Feature } from "../types/features";
import { Language } from "../i18n";

export interface CustomerConfigElement {
    portalArn: string;
    customerLandingPage: string;
    authenticationType: AuthType;
    isFipsEnabled?: boolean;
    portalCustomDomain?: string;
    customerDomain: string;
}

export interface PortalConfig {
    portalArn: string;
    isFipsEnabled: boolean;
    customerDomain: string;
    portalCustomDomain?: string;
}

export interface ToolbarConfiguration {
    toolbarType: string;
    hiddenToolbarItems: string[];
    maxDisplayResolution: string;
    visualMode: string;
}

export interface BrandingConfiguration {
    userSettingId: string;
    logoExtension: string;
    wallpaperExtension?: string;
    faviconExtension: string;
    localizedStrings: Record<Language, LocalizedBrandingStrings>;
    colorTheme: ColorTheme;
    isTermsOfServiceProvided: boolean;
}

export enum ColorTheme {
    LIGHT = "Light",
    DARK = "Dark",
}

export interface LocalizedBrandingStrings {
    browserTabTitle: string;
    welcomeText: string;
    loginTitle?: string;
    loginDescription?: string;
    loginButtonText?: string;
    contactLink?: string;
    contactButtonText?: string;
    loadingText?: string;
}

export interface PortalSettings {
    toolbarConfiguration?: ToolbarConfiguration;
    brandingConfiguration: BrandingConfiguration;
    isCookieSyncEnabled?: boolean;
    isDeepLinkEnabled?: boolean;
}

export interface EnvInfo {
    stage: string;
    region: string;
    loggerEndpoint: string;
}

export const DEFAULT_CLIENT_CONFIG: ConfigurationOptions = {
    // Currently, no SSR APIs use SigV4 and instead rely on OAuth tokens that are sent in the request as cookies.
    signatureVersion: "v4",
    sslEnabled: true,
    maxRetries: 1,
    // https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/Config.html#correctClockSkew-property
    // https://aws.amazon.com/blogs/developer/clock-skew-correction/
    correctClockSkew: true,
};

export const getPortalConfig = (): PortalConfig => {
    const customerConfigElement = document.getElementById(
        CONFIG_ELEMENTS.CUSTOMER_CONFIG
    );
    if (customerConfigElement) {
        const rawConfig = JSON.parse(
            customerConfigElement.innerHTML
        ) as CustomerConfigElement;

        return {
            portalArn: rawConfig.portalArn,
            isFipsEnabled: rawConfig.isFipsEnabled,
            portalCustomDomain: rawConfig.portalCustomDomain,
            customerDomain: rawConfig.customerDomain,
        };
    } else {
        log.warn(
            "Getting Portal config for local development, can safely ignore if WebApp running locally."
        );
        return {
            portalArn: "",
            isFipsEnabled: false,
            portalCustomDomain: null,
            customerDomain: "",
        };
    }
};

export const getEnvInfo = (): EnvInfo => {
    const environmentConfigElement = document.getElementById(
        CONFIG_ELEMENTS.ENV_INFO
    );

    if (!environmentConfigElement) {
        throw new Error("env-info element not found in HTML");
    }

    const environmentConfig = JSON.parse(environmentConfigElement.innerHTML);
    const { stage, region, loggerEndpoint = null } = environmentConfig;

    return { stage, region, loggerEndpoint };
};

export const isLocalhost = (): boolean => {
    return window.location.hostname === "localhost";
};

export const getPortalEndpoint = (): string => {
    const { stage } = getEnvInfo();
    const { portalArn, isFipsEnabled, portalCustomDomain } = getPortalConfig();

    // if portal is being accessed using custom domain
    // we will use portal custom domain as portal endpoint
    if (
        portalCustomDomain !== null &&
        window.location.hostname === portalCustomDomain
    ) {
        return `https://${portalCustomDomain}`;
    }

    const arnParts = portalArn.split(":");
    const portalId = arnParts.slice(-1).pop()!.split("/").slice(-1).pop();
    const stageSubdomain = stage.toLowerCase() === "prod" ? "" : `.${stage}`;
    const fips = isFipsEnabled ? "-fips" : "";

    return `https://${portalId}${stageSubdomain}.workspaces-web${fips}.com`;
};

export const isFeatureEnabled = (feature: Feature): boolean => {
    const featuresElement = document.getElementById(CONFIG_ELEMENTS.FEATURES);

    if (featuresElement) {
        const features = JSON.parse(featuresElement.innerHTML) as string[];
        return features?.includes(feature);
    }
    return false;
};

export const getToolbarConfig = (): ToolbarConfiguration => {
    const settingsElement = document.getElementById(CONFIG_ELEMENTS.SETTINGS);
    if (settingsElement) {
        const settings = JSON.parse(
            settingsElement.innerHTML
        ) as PortalSettings;
        const toolbarConfiguration = settings?.toolbarConfiguration as ToolbarConfiguration;
        return {
            toolbarType: toolbarConfiguration?.toolbarType || undefined,
            hiddenToolbarItems: toolbarConfiguration?.hiddenToolbarItems || [],
            maxDisplayResolution:
                toolbarConfiguration?.maxDisplayResolution || "",
            visualMode:
                toolbarConfiguration?.visualMode?.toLowerCase() || undefined,
        };
    }

    return {
        toolbarType: undefined,
        hiddenToolbarItems: [],
        maxDisplayResolution: "",
        visualMode: undefined,
    };
};

export const getCustomBrandingConfig = ():
    | BrandingConfiguration
    | undefined => {
    const settingsElement = document.getElementById(CONFIG_ELEMENTS.SETTINGS);
    if (settingsElement) {
        const settings = JSON.parse(
            settingsElement.innerHTML
        ) as PortalSettings;
        return settings?.brandingConfiguration;
    } else {
        return undefined;
    }
};
